import React, { useEffect, useRef, useState } from 'react';
import './BingoScanner.css';
import { useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { appendAuditEvent } from '../utils/auditTrail';
import {
  getBingoPattern,
  getPatternLabel,
} from '../data/bingoPatterns';

function getBingoLetter(number) {
  const value = Number(number);

  if (!Number.isInteger(value) || value < 1 || value > 75) {
    return null;
  }

  if (value <= 15) return 'B';
  if (value <= 30) return 'I';
  if (value <= 45) return 'N';
  if (value <= 60) return 'G';
  return 'O';
}

function normalizeBingoBall(ball) {
  let number = null;

  if (typeof ball === 'number') {
    number = ball;
  } else if (typeof ball === 'string') {
    const match = ball.trim().toUpperCase().match(/^(?:[BINGO]\s*)?(\d{1,2})$/);
    number = match ? Number(match[1]) : null;
  } else if (ball && typeof ball === 'object') {
    number = Number(ball.number);
  }

  const letter = getBingoLetter(number);

  return letter
    ? { letter, number: Number(number) }
    : null;
}

function BingoBall({ ball, size = 'medium' }) {
  const normalized = normalizeBingoBall(ball);
  const sizeClass = ['small', 'medium', 'large'].includes(size)
    ? size
    : 'medium';

  if (!normalized) {
    return (
      <div
        className={`live-bingo-ball live-bingo-ball-${sizeClass} live-bingo-ball-empty`}
        aria-label="No bingo ball"
      >
        <span className="live-bingo-ball-number">—</span>
      </div>
    );
  }

  return (
    <div
      className={`live-bingo-ball live-bingo-ball-${sizeClass} live-bingo-ball-${normalized.letter.toLowerCase()}`}
      aria-label={`${normalized.letter} ${normalized.number}`}
    >
      <span className="live-bingo-ball-letter">{normalized.letter}</span>
      <span className="live-bingo-ball-number">{normalized.number}</span>
    </div>
  );
}

function BingoScanner() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  const [captureSource, setCaptureSource] =
  useState(null);

  const [quality, setQuality] = useState({
    checked: false,
    acceptable: false,
    sharpness: null,
    width: null,
    height: null,
    message: '',
  });

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const { eventId } = useParams();

const [gameSession, setGameSession] = useState(null);
const [gameLoading, setGameLoading] = useState(true);
const [gameError, setGameError] = useState('');
const [verification, setVerification] = useState(null);

  // Clean up camera and preview URL when component unmounts.
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current
          .getTracks()
          .forEach(track => track.stop());
      }

      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  // Attach the active camera stream after the video element renders.
  useEffect(() => {
    if (
      !cameraOpen ||
      !videoRef.current ||
      !streamRef.current
    ) {
      return;
    }

    const video = videoRef.current;

    video.srcObject = streamRef.current;

    let attempts = 0;
    let cancelled = false;
    let timeoutId = null;

    const checkCameraReady = async () => {
      if (cancelled) {
        return;
      }

      try {
        await video.play();
      } catch (error) {
        console.error(
          'Video play error:',
          error
        );
      }

      if (
        video.videoWidth > 0 &&
        video.videoHeight > 0
      ) {
        console.log(
          'Camera ready:',
          video.videoWidth,
          video.videoHeight
        );

        setCameraReady(true);
        return;
      }

      attempts++;

      if (attempts < 30) {
        timeoutId = setTimeout(
          checkCameraReady,
          200
        );
      } else {
        console.error(
          'Camera stream started but video dimensions never became available.'
        );

        setError(
          'Camera started, but the video feed could not initialize. Try closing and reopening the camera.'
        );
      }
    };

    checkCameraReady();

    return () => {
      cancelled = true;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      setCameraReady(false);
    };
  }, [cameraOpen]);

  useEffect(() => {
  if (!eventId) {
    setGameError('No bingo event was selected.');
    setGameLoading(false);
    return;
  }

  const eventRef = doc(
    db,
    'bingoMachineEvents',
    eventId
  );

  const unsubscribe = onSnapshot(
    eventRef,
    snapshot => {
      if (!snapshot.exists()) {
        setGameSession(null);
        setGameError(
          'This bingo event could not be found.'
        );
        setGameLoading(false);
        return;
      }

      const data = snapshot.data();

      setGameSession({
        id: snapshot.id,
        ...data,
      });

      setGameError('');
      setGameLoading(false);
    },
    error => {
      console.error(
        'Scanner event sync error:',
        error
      );

      setGameError(
        'Unable to connect to the bingo event.'
      );

      setGameLoading(false);
    }
  );

  return () => unsubscribe();
}, [eventId]);

  function clearQuality() {
    setQuality({
      checked: false,
      acceptable: false,
      sharpness: null,
      width: null,
      height: null,
      message: '',
    });
  }

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current
        .getTracks()
        .forEach(track => track.stop());

      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraReady(false);
    setCameraOpen(false);
  }

  async function openCamera() {
    setError('');
    setResult(null);
    setCameraReady(false);

    try {
      if (
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        throw new Error(
          'Camera access is not supported by this browser.'
        );
      }

      stopCamera();

      const stream =
        await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: {
              ideal: 'environment',
            },
            width: {
              ideal: 3840,
            },
            height: {
              ideal: 2160,
            },
          },
          audio: false,
        });

      streamRef.current = stream;
      setCameraOpen(true);
    } catch (err) {
      console.error(
        'Camera error:',
        err
      );

      setError(
        `${err.name || 'CameraError'}: ${
          err.message ||
          'Unable to access the camera.'
        }`
      );

      stopCamera();
    }
  }

  async function evaluateImageQuality(imageFile) {
    return new Promise(resolve => {
      const image = new Image();

      const imageUrl =
        URL.createObjectURL(imageFile);

      image.onload = () => {
        const analysisWidth = 500;

        const scale =
          analysisWidth / image.width;

        const analysisHeight = Math.max(
          1,
          Math.round(
            image.height * scale
          )
        );

        const canvas =
          document.createElement(
            'canvas'
          );

        canvas.width =
          analysisWidth;

        canvas.height =
          analysisHeight;

        const ctx =
          canvas.getContext(
            '2d',
            {
              willReadFrequently: true,
            }
          );

        if (!ctx) {
          URL.revokeObjectURL(
            imageUrl
          );

          resolve({
            checked: true,
            acceptable: false,
            sharpness: null,
            width: image.width,
            height: image.height,
            message:
              'Could not analyze photo quality.',
          });

          return;
        }

        ctx.drawImage(
          image,
          0,
          0,
          analysisWidth,
          analysisHeight
        );

        const imageData =
          ctx.getImageData(
            0,
            0,
            analysisWidth,
            analysisHeight
          );

        const data =
          imageData.data;

        const grayscale =
          new Float32Array(
            analysisWidth *
            analysisHeight
          );

        for (
          let i = 0;
          i < data.length;
          i += 4
        ) {
          const pixelIndex =
            i / 4;

          grayscale[pixelIndex] =
            0.299 * data[i] +
            0.587 * data[i + 1] +
            0.114 * data[i + 2];
        }

        let sum = 0;
        let sumSquares = 0;
        let count = 0;

        for (
          let y = 1;
          y < analysisHeight - 1;
          y++
        ) {
          for (
            let x = 1;
            x < analysisWidth - 1;
            x++
          ) {
            const i =
              y * analysisWidth + x;

            const laplacian =
              grayscale[
                i - analysisWidth
              ] +
              grayscale[
                i + analysisWidth
              ] +
              grayscale[i - 1] +
              grayscale[i + 1] -
              4 * grayscale[i];

            sum += laplacian;

            sumSquares +=
              laplacian *
              laplacian;

            count++;
          }
        }

        const mean =
          count > 0
            ? sum / count
            : 0;

        const variance =
          count > 0
            ? sumSquares /
                count -
              mean * mean
            : 0;

        const width =
          image.width;

        const height =
          image.height;

        URL.revokeObjectURL(
          imageUrl
        );

        const minimumWidth =
          1200;

        const minimumHeight =
          1200;

        const minimumSharpness =
          100;

        if (
          width < minimumWidth ||
          height < minimumHeight
        ) {
          resolve({
            checked: true,
            acceptable: false,
            sharpness: variance,
            width,
            height,
            message:
              'Photo resolution is too low. Move closer and retake the card.',
          });

          return;
        }

        if (
          variance <
          minimumSharpness
        ) {
          resolve({
            checked: true,
            acceptable: false,
            sharpness: variance,
            width,
            height,
            message:
              'Photo looks blurry. Hold the phone steady and retake it.',
          });

          return;
        }

        resolve({
          checked: true,
          acceptable: true,
          sharpness: variance,
          width,
          height,
          message:
            'Photo quality looks good.',
        });
      };

      image.onerror = () => {
        URL.revokeObjectURL(
          imageUrl
        );

        resolve({
          checked: true,
          acceptable: false,
          sharpness: null,
          width: null,
          height: null,
          message:
            'Could not analyze the photo.',
        });
      };

      image.src = imageUrl;
    });
  }

  async function capturePhoto() {
  setError('');

  const video = videoRef.current;

  if (!video) {
    setError('Camera is not ready.');
    return;
  }

  /*
   * Give autofocus and exposure a little
   * more time to settle before starting
   * the burst.
   */
  await new Promise(resolve =>
    setTimeout(resolve, 600)
  );

  const videoWidth = video.videoWidth;
  const videoHeight = video.videoHeight;

  if (!videoWidth || !videoHeight) {
    setError(
      'Camera image is not ready yet.'
    );

    return;
  }

  /*
   * Determine the source crop once.
   *
   * This matches the visible 4:5 camera
   * frame and the dashed guide.
   */
  const targetAspect = 4 / 5;

  const sourceAspect =
    videoWidth / videoHeight;

  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = videoWidth;
  let sourceHeight = videoHeight;

  if (sourceAspect > targetAspect) {
    sourceWidth =
      videoHeight * targetAspect;

    sourceX =
      (videoWidth - sourceWidth) / 2;
  } else {
    sourceHeight =
      videoWidth / targetAspect;

    sourceY =
      (videoHeight - sourceHeight) / 2;
  }

  /*
   * Match the dashed visual guide.
   */
/*
 * Keep a small amount of breathing room
 * around the visual guide.
 *
 * The backend dynamically detects the actual
 * bingo grid, so preserving extra context is
 * preferable to accidentally clipping the card.
 */
const guideInsetX = 0.015;
const guideInsetY = 0.015;

sourceX +=
  sourceWidth * guideInsetX;

sourceY +=
  sourceHeight * guideInsetY;

sourceWidth *=
  1 - guideInsetX * 2;

sourceHeight *=
  1 - guideInsetY * 2;

  /*
   * Capture one frame from the video.
   */
  async function captureFrame() {
    const canvas =
      document.createElement('canvas');

    canvas.width =
      Math.round(sourceWidth);

    canvas.height =
      Math.round(sourceHeight);

    const context =
      canvas.getContext('2d');

    if (!context) {
      throw new Error(
        'Could not capture the camera image.'
      );
    }

    context.drawImage(
      video,

      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,

      0,
      0,
      canvas.width,
      canvas.height
    );

    const blob =
      await new Promise(resolve => {
        canvas.toBlob(
          resolve,
          'image/jpeg',
          0.98
        );
      });

    if (!blob) {
      throw new Error(
        'Could not capture photo.'
      );
    }

    const capturedFile =
      new File(
        [blob],
        `bingo-card-${Date.now()}.jpg`,
        {
          type: 'image/jpeg',
        }
      );

    const qualityResult =
      await evaluateImageQuality(
        capturedFile
      );

    return {
      file: capturedFile,
      quality: qualityResult,
      width: canvas.width,
      height: canvas.height,
    };
  }

  try {
    /*
     * Capture a short burst.
     *
     * 3 frames gives us protection from
     * hand movement without making capture
     * feel noticeably slow.
     */
    const frames = [];

    for (let i = 0; i < 3; i++) {
      const frame =
        await captureFrame();

      frames.push(frame);

      if (i < 2) {
        await new Promise(resolve =>
          setTimeout(resolve, 140)
        );
      }
    }

    /*
     * Prefer frames that passed the
     * quality gate.
     *
     * Within that group, choose the
     * highest sharpness score.
     */
    const acceptableFrames =
      frames.filter(
        frame =>
          frame.quality.acceptable
      );

    const candidates =
      acceptableFrames.length > 0
        ? acceptableFrames
        : frames;

    candidates.sort(
      (a, b) =>
        (b.quality.sharpness || 0) -
        (a.quality.sharpness || 0)
    );

    const bestFrame =
      candidates[0];

    if (!bestFrame) {
      throw new Error(
        'Could not capture a usable photo.'
      );
    }

    if (preview) {
      URL.revokeObjectURL(preview);
    }

    const previewUrl =
      URL.createObjectURL(
        bestFrame.file
      );

    setFile(bestFrame.file);
    setPreview(previewUrl);
    setCaptureSource('camera');

    setResult(null);
    setVerification(null);
    setError('');

    /*
     * We already evaluated this image,
     * so don't analyze it a second time.
     */
    setQuality(
      bestFrame.quality
    );

    console.log(
      'Camera burst:',
      frames.map(
        (frame, index) => ({
          frame: index + 1,

          sharpness:
            frame.quality.sharpness,

          acceptable:
            frame.quality.acceptable,

          width:
            frame.width,

          height:
            frame.height,
        })
      )
    );

    console.log(
      'Selected camera frame:',
      {
        sharpness:
          bestFrame.quality.sharpness,

        acceptable:
          bestFrame.quality.acceptable,

        width:
          bestFrame.width,

        height:
          bestFrame.height,
      }
    );

    stopCamera();
  } catch (err) {
    console.error(
      'Camera capture error:',
      err
    );

    setError(
      err.message ||
        'Could not capture photo.'
    );
  }
}

  async function handleFileChange(event) {
    const selectedFile =
      event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    stopCamera();

    if (preview) {
      URL.revokeObjectURL(
        preview
      );
    }

    clearQuality();

    setFile(
      selectedFile
    );

    setCaptureSource('upload');

    setPreview(
      URL.createObjectURL(
        selectedFile
      )
    );

    setResult(null);
    setError('');

    const qualityResult =
      await evaluateImageQuality(
        selectedFile
      );

    setQuality(
      qualityResult
    );

    console.log(
      'Uploaded image quality:',
      qualityResult
    );
  }

  function deserializePattern(flatPattern) {
  if (
    !Array.isArray(flatPattern) ||
    flatPattern.length !== 25
  ) {
    return null;
  }

  const pattern = [];

  for (let row = 0; row < 5; row++) {
    pattern.push(
      flatPattern.slice(
        row * 5,
        row * 5 + 5
      )
    );
  }

  return pattern;
}

function verifyScannedCard(scanResult, session) {
  if (!scanResult?.card || !scanResult?.daubs) {
    return {
      status: 'review',
      verified: false,
      message: 'Card scan is incomplete.',
    };
  }

  if (!session) {
    return {
      status: 'review',
      verified: false,
      message: 'No active bingo game found.',
    };
  }

  const calledNumbers = new Set(
    (session.generatedNumbers || [])
      .map(ball => Number(ball.number))
  );

  let patternMasks = [];

  if (session.selectedPattern === 'custom') {
    const customPattern = deserializePattern(
      session.customPattern
    );

    if (customPattern) {
      patternMasks = [customPattern];
    }
  } else {
    const builtInPattern = getBingoPattern(
      session.selectedPattern
    );

    patternMasks = (
      builtInPattern?.masks || []
    ).filter(mask => (
      Array.isArray(mask) &&
      mask.length === 5 &&
      mask.every(row => (
        Array.isArray(row) &&
        row.length === 5
      )) &&
      mask.some(row => row.some(Boolean))
    ));
  }

  if (patternMasks.length === 0) {
    return {
      status: 'review',
      verified: false,
      message:
        'No valid winning pattern is available for this game.',
    };
  }

  const uncalledDaubs = [];

  /*
   * First verify all detected player daubs.
   */
  for (let row = 0; row < 5; row++) {
    for (let column = 0; column < 5; column++) {
      const isFree =
        row === 2 &&
        column === 2;

      if (isFree) {
        continue;
      }

      const isDaubed =
        scanResult.daubs[row][column];

      const number =
        scanResult.card[row][column];

      if (
        isDaubed &&
        !calledNumbers.has(Number(number))
      ) {
        uncalledDaubs.push({
          row,
          column,
          number,
        });
      }
    }
  }

  /*
   * Evaluate each valid mask independently. Patterns such as
   * horizontal, postage stamp, and arrow contain alternative
   * masks; satisfying any one of them is a valid pattern win.
   */
  const maskResults = patternMasks.map(pattern => {
    const missingPatternCells = [];
    const invalidPatternNumbers = [];

    for (let row = 0; row < 5; row++) {
      for (let column = 0; column < 5; column++) {
        if (!pattern[row][column]) {
          continue;
        }

        const isFree =
          row === 2 &&
          column === 2;

        if (isFree) {
          continue;
        }

        const isDaubed =
          scanResult.daubs[row][column];

        const number =
          scanResult.card[row][column];

        if (!isDaubed) {
          missingPatternCells.push({
            row,
            column,
            number,
          });

          continue;
        }

        if (
          !calledNumbers.has(Number(number))
        ) {
          invalidPatternNumbers.push({
            row,
            column,
            number,
          });
        }
      }
    }

    return {
      missingPatternCells,
      invalidPatternNumbers,
    };
  });

  const matchingMask = maskResults.find(
    ({
      missingPatternCells,
      invalidPatternNumbers,
    }) => (
      missingPatternCells.length === 0 &&
      invalidPatternNumbers.length === 0
    )
  );

  /*
   * Required pattern failed.
   */
  if (!matchingMask) {
    const closestMask = [...maskResults].sort(
      (first, second) => (
        first.missingPatternCells.length +
        first.invalidPatternNumbers.length
      ) - (
        second.missingPatternCells.length +
        second.invalidPatternNumbers.length
      )
    )[0];

    return {
      status: 'invalid',
      verified: false,
      message:
        'This card does not match the winning pattern.',
      missingPatternCells:
        closestMask.missingPatternCells,
      invalidPatternNumbers:
        closestMask.invalidPatternNumbers,
      uncalledDaubs,
    };
  }

  /*
   * Pattern is good, but scanner detected
   * extra daubs on numbers that were not called.
   *
   * Because camera daub detection can occasionally
   * over-detect, send this to human review rather
   * than automatically rejecting the winner.
   */
  if (
    uncalledDaubs.length > 0 ||
    scanResult.uncertainCells?.length > 0
  ) {
    return {
      status: 'review',
      verified: false,
      message:
        'Winning pattern detected, but the card needs a quick review.',
      uncalledDaubs,
    };
  }

  return {
    status: 'verified',
    verified: true,
    message: 'Card Verified',
  };
}

  async function scanCard() {
    if (!file) {
      setError(
        'Take or choose a photo first.'
      );

      return;
    }

    if (
      !quality.checked ||
      !quality.acceptable
    ) {
      setError(
        'Please use a clearer photo before scanning.'
      );

      return;
    }

    setScanning(true);
    setError('');
    setResult(null);

    try {
      const formData =
        new FormData();

      formData.append(
        'image',
        file
      );

      const response =
        await fetch(
          '/api/scan',
          {
            method: 'POST',
            body: formData,
          }
        );

      /*
       * Read as text first because Vercel can
       * return plain text when a serverless
       * function crashes.
       */
      const responseText =
        await response.text();

      let data = {};

      if (responseText) {
        try {
          data =
            JSON.parse(
              responseText
            );
        } catch (parseError) {
          console.error(
            'Non-JSON scanner response:',
            responseText
          );

          if (!response.ok) {
            throw new Error(
              `Scanner server error (${response.status}): ${responseText}`
            );
          }

          throw new Error(
            'The scanner returned an invalid response.'
          );
        }
      }

if (!response.ok) {
  throw new Error(
    data.details ||
    data.error ||
    `Card scan failed (${response.status}).`
  );
}

if (
  captureSource === 'camera' &&
  data.needsRescan
) {
  console.warn(
    'Camera scan rejected by OCR quality gate:',
    {
      unresolvedCount:
        data.unresolvedCount,
      contextRecoveryCount:
        data.contextRecoveryCount,
    }
  );

  setResult(null);
  setVerification(null);

  appendAuditEvent({
    eventId,
    game: Array.isArray(gameSession?.gameSchedule) &&
      Number.isInteger(gameSession?.activeGameIndex)
      ? gameSession.gameSchedule[gameSession.activeGameIndex]
      : null,
    type: 'CARD_VERIFIED',
    payload: {
      result: 'REVIEW',
      scannerStatus: 'quality-gate',
      verified: false,
      patternId: gameSession?.selectedPattern || null,
      source: captureSource || 'scanner',
    },
  });

  setError(
    "We couldn't read this card clearly. Retake the photo with the full card visible and good lighting."
  );

  return;
}

/*
 * Temporary daub diagnostics.
 */
console.table(
  (data.cells || []).map(cell => ({
    number: cell.value,
    row: cell.row,
    column: cell.column,
    daubed: cell.daubed,
    score: cell.daubScore,
    colorCoverage:
      cell.daubDetection?.colorCoverage,
    strongCoverage:
      cell.daubDetection?.strongColorCoverage,
    avgSaturation:
      cell.daubDetection?.averageSaturation,
  }))
);

setResult(data);

const verificationResult =
  verifyScannedCard(
    data,
    gameSession
  );

setVerification(
  verificationResult
);

const activeAuditGame =
  Array.isArray(gameSession?.gameSchedule) &&
  Number.isInteger(gameSession?.activeGameIndex)
    ? gameSession.gameSchedule[gameSession.activeGameIndex]
    : {
        id: `game-${gameSession?.gameNumber || 1}`,
        gameNumber: gameSession?.gameNumber || 1,
        name: `Game ${gameSession?.gameNumber || 1}`,
        pattern: gameSession?.selectedPattern || null,
        prizeAmount: gameSession?.prizeAmount || '',
      };

const auditResult =
  verificationResult.status === 'verified'
    ? 'WINNER'
    : verificationResult.status === 'invalid'
      ? 'NOT_WINNER'
      : verificationResult.status === 'review'
        ? 'REVIEW'
        : 'INVALID';

appendAuditEvent({
  eventId,
  game: activeAuditGame,
  type: 'CARD_VERIFIED',
  payload: {
    result: auditResult,
    scannerStatus: verificationResult.status,
    verified: Boolean(verificationResult.verified),
    patternId: gameSession?.selectedPattern || null,
    cardIdentifier: data.cardIdentifier || data.cardNumber || null,
    source: captureSource || 'scanner',
  },
});

console.log(
  'Scanner diagnostics:',
  data.diagnostics
);

console.log(
  'Scanner timings:',
  data.diagnostics?.timings
);

    } catch (err) {
      console.error(
        'Scanner error:',
        err
      );

      setError(
        err.message ||
        'Card scan failed.'
      );

    } finally {
      setScanning(false);
    }
  }

  function resetScanner() {
    stopCamera();

    if (preview) {
      URL.revokeObjectURL(
        preview
      );
    }

   setFile(null);
setPreview(null);
setResult(null);
setVerification(null);
setError('');
setCaptureSource(null);

    clearQuality();

    const galleryInput =
      document.getElementById(
        'bingo-gallery-input'
      );

    if (galleryInput) {
      galleryInput.value = '';
    }
  }

  const recentCalledBalls =
    Array.isArray(gameSession?.generatedNumbers)
      ? gameSession.generatedNumbers.slice(-5).reverse()
      : [];

  return (
    <div className="bingo-scanner">
        {gameLoading && (
  <div className="scanner-event-status">
    Connecting to bingo event...
  </div>
)}

{gameError && (
  <div className="scanner-error">
    {gameError}
  </div>
)}

{gameSession && (
  <div className="scanner-event-status">
    <strong>
      {gameSession.eventName}
    </strong>

    <div>
      Game {gameSession.gameNumber}
      {' • '}
      Pattern: {
        gameSession.selectedPattern
          ? getPatternLabel(gameSession.selectedPattern)
          : 'None'
      }
      {' • '}
      Called balls: {
        gameSession.generatedNumbers?.length || 0
      }
    </div>

    <div className="scanner-live-ball-status">
      <div className="scanner-recent-calls">
        <span className="scanner-live-ball-label">Recent Calls</span>

        {recentCalledBalls.length > 0 ? (
          <div className="scanner-recent-ball-list">
            {recentCalledBalls.map((ball, index) => {
              const normalized = normalizeBingoBall(ball);

              return (
                <BingoBall
                  key={`scanner-recent-${normalized?.number ?? index}`}
                  ball={ball}
                  size={index === 0 ? 'medium' : 'small'}
                />
              );
            })}
          </div>
        ) : (
          <span className="scanner-live-ball-empty-text">No balls called</span>
        )}
      </div>

      <div className="scanner-next-ball">
        <span className="scanner-live-ball-label">Next Ball Up</span>
        <BingoBall ball={gameSession.nextBall} size="medium" />
      </div>
    </div>
  </div>
)}
      <div className="scanner-header">
        <h1>
          Bingo Card Verification
        </h1>

        <p>
          Take a clear photo of the
          entire bingo card.
        </p>
      </div>

      {!result && (
        <>
          {!cameraOpen && (
            <div className="scanner-photo-actions">

              <button
                type="button"
                className="scanner-upload scanner-camera-button"
                onClick={openCamera}
              >
                Take Photo
              </button>

              <label
                className="scanner-upload scanner-gallery-button"
                htmlFor="bingo-gallery-input"
              >
                Choose Photo
              </label>

              <input
                id="bingo-gallery-input"
                className="scanner-file-input"
                type="file"
                accept="image/*"
                onChange={
                  handleFileChange
                }
              />

            </div>
          )}

          {cameraOpen && (
            <div className="scanner-camera">

              <div className="scanner-camera-frame">
                <video
                  ref={videoRef}
                  className="scanner-video"
                  playsInline
                  muted
                  autoPlay
                />
              </div>

              <p className="scanner-camera-help">
                Position the entire bingo card
                inside the frame and hold steady.
              </p>

              <div className="scanner-camera-controls">

                <button
                  type="button"
                  className="scanner-capture-button"
                  onClick={
                    capturePhoto
                  }
                  disabled={
                    !cameraReady
                  }
                >
                  {cameraReady
                    ? 'Capture'
                    : 'Starting Camera...'}
                </button>

                <button
                  type="button"
                  className="scanner-camera-cancel"
                  onClick={
                    stopCamera
                  }
                >
                  Cancel
                </button>

              </div>
            </div>
          )}

          {preview &&
            !cameraOpen && (
              <div className="scanner-preview">
                <img
                  src={preview}
                  alt="Bingo card preview"
                />
              </div>
            )}

          {file &&
            !cameraOpen &&
            !quality.checked && (
              <div className="scanner-quality">
                Checking photo quality...
              </div>
            )}

          {file &&
            !cameraOpen &&
            quality.checked && (
              <div
                className={
                  quality.acceptable
                    ? 'scanner-quality scanner-quality-good'
                    : 'scanner-quality scanner-quality-bad'
                }
              >
                <strong>
                  {quality.acceptable
                    ? 'Photo Ready'
                    : 'Retake Recommended'}
                </strong>

                <div>
                  {quality.message}
                </div>

                {quality.sharpness !==
                  null && (
                    <div className="scanner-quality-details">
                      Sharpness:{' '}
                      {quality.sharpness.toFixed(
                        1
                      )}
                    </div>
                  )}
              </div>
            )}

          {file &&
            !cameraOpen && (
              <button
                className="scanner-button"
                onClick={
                  scanCard
                }
                disabled={
                  scanning ||
                  !quality.checked ||
                  !quality.acceptable
                }
              >
                {scanning
                  ? 'Scanning Card...'
                  : 'Scan Card'}
              </button>
            )}
        </>
      )}

     {scanning && (
  <div className="scanner-status">
    <div className="scanner-processing-card">
      <div className="scanner-processing-grid">
        {Array.from({ length: 25 }).map((_, index) => (
          <span
            key={index}
            className="scanner-processing-cell"
          />
        ))}
      </div>

      <div className="scanner-scan-beam" />
    </div>

    <div className="scanner-processing-copy">
      <strong>Analyzing bingo card</strong>

      <div className="scanner-processing-steps">
        <span>Reading numbers</span>
        <span>Detecting daubs</span>
        <span>Verifying card</span>
      </div>
    </div>
  </div>
)}

      {error && (
        <div className="scanner-error">
          {error}
        </div>
      )}

      {result?.card && (
        <div className="scanner-result">
            {verification && (
  <div
    className={`scanner-verification scanner-verification-${verification.status}`}
  >
    <div className="scanner-verification-icon">
      {verification.status === 'verified'
        ? '✓'
        : verification.status === 'review'
        ? '!'
        : '✕'}
    </div>

    <strong>
      {verification.status === 'verified'
        ? 'Card Verified'
        : verification.status === 'review'
        ? 'Check Card'
        : 'Not Verified'}
    </strong>

    <div>
      {verification.message}
    </div>

    {verification.uncalledDaubs?.length > 0 && (
      <div className="scanner-verification-details">
        Uncalled marked numbers:{' '}
        {verification.uncalledDaubs
          .map(cell => cell.number)
          .join(', ')}
      </div>
    )}

    {verification.missingPatternCells?.length > 0 && (
      <div className="scanner-verification-details">
        Missing pattern spaces:{' '}
        {verification.missingPatternCells
          .map(cell => cell.number)
          .join(', ')}
      </div>
    )}

    {verification.invalidPatternNumbers?.length > 0 && (
      <div className="scanner-verification-details">
        Pattern numbers not called:{' '}
        {verification.invalidPatternNumbers
          .map(cell => cell.number)
          .join(', ')}
      </div>
    )}
  </div>
)}
          <h2>
            {result.success
              ? 'Card Recognized'
              : 'Card Needs Review'}
          </h2>

          <div className="scanner-grid">

            {[
              'B',
              'I',
              'N',
              'G',
              'O',
            ].map(letter => (
              <div
                className="scanner-grid-header"
                key={letter}
              >
                {letter}
              </div>
            ))}

            {result.card.flatMap(
              (row, rowIndex) =>
                row.map(
                  (
                    value,
                    columnIndex
                  ) => {
                    const marked =
                      result.daubs?.[
                        rowIndex
                      ]?.[
                        columnIndex
                      ] === true;

                    const classes = [
                      'scanner-cell',

                      value === 'FREE'
                        ? 'scanner-free'
                        : '',

                      marked
                        ? 'scanner-cell-daubed'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    return (
                      <div
                        className={
                          classes
                        }
                        key={`${rowIndex}-${columnIndex}`}
                      >
                        <span className="scanner-cell-value">
                          {value ?? '?'}
                        </span>
                      </div>
                    );
                  }
                )
            )}

          </div>

          {result.daubedCells && (
            <div className="scanner-daub-summary">
              Detected marked spaces:{' '}
              <strong>
                {
                  result
                    .daubedCells
                    .length
                }
              </strong>
            </div>
          )}

          {result
            .uncertainCells
            ?.length > 0 && (
              <div className="scanner-review">
                <strong>
                  Some numbers need review.
                </strong>

                {result
                  .uncertainCells
                  .map(cell => (
                    <div
                      key={`${cell.row}-${cell.column}`}
                    >
                      Row {cell.row},
                      {' '}
                      column {
                        cell.column
                      }
                    </div>
                  ))}
              </div>
            )}

          <button
            className="scanner-button scanner-again"
            onClick={
              resetScanner
            }
          >
            Reset Scanner
          </button>

        </div>
      )}

    </div>
  );
}

export default BingoScanner

